import {type TranslationObject} from '@colonist/i18n'
import {type UIGameManager} from '../UIGameManager.ts'
import {BeginnerModeLevel} from '@colonist/game-beginner-mode'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView.ts'
import {UIGameAchievementBackgroundView} from '../ui_game_achievement/UIGameAchievementBackgroundView.ts'
import {UIGameBeginnerModeGoalView} from './UIGameBeginnerModeGoalView.ts'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler.ts'
import {type PIXI, type ScrollBoxOptions, Scrollbox} from '@colonist/pixi'
import {Sprites} from '../UIGameImageDictionary.ts'
import {CardEnum} from '@colonist/game-states'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables.ts'

interface BeginnerModeGoal {
    text: TranslationObject
    icon: PIXI.Texture | undefined
    requiredResources?: CardEnum[]
}

export class UIGameBeginnerModeGoalsController {
    private readonly uiGameManager: UIGameManager

    private scrollbox: Scrollbox
    mainContainer: SectionBgView
    private readonly goals: BeginnerModeGoal[]
    private goalViews: UIGameBeginnerModeGoalView[] = []
    private readonly goalHeight: number
    private readonly itemSize: number
    private readonly renderMobileGoals: boolean = false

    constructor(uiGameManager: UIGameManager, level: BeginnerModeLevel = BeginnerModeLevel.Level1) {
        this.uiGameManager = uiGameManager
        this.itemSize = this.uiGameManager.canvasController.itemSize * 0.8
        this.goalHeight = this.itemSize * 0.65
        this.goals = this.getGoals()
        this.renderMobileGoals = UIUtilsEjsVariables.isUserOnMobileAppOrWeb && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI && !UIUtilsEjsVariables.isUserOnTabletApp
        if(this.goals.length == 0) return

        this.initMainContainer()
        this.drawBackground()
        this.createScrollbox()
        this.createGoalViews()
    }

    private initMainContainer() {
        const {containerX, containerY, containerWidth, containerHeight} = this.renderMobileGoals ? this.getMainContainerPositionMobile() : this.getMainContainerPositionGeneric()
        this.mainContainer = new SectionBgView(containerX, containerY, containerWidth, containerHeight, this.uiGameManager.canvasController.app.stage)
    }

    private getMainContainerPositionGeneric(): {containerX: number, containerY: number, containerWidth: number, containerHeight: number} {
        const itemSize = this.itemSize
        const margin = this.uiGameManager.canvasController.margin
        const containerX = 0
        const containerY = itemSize * 2
        const containerWidth = itemSize * 3.8
        const containerHeight = this.goalHeight * (this.goals.length - 1) + margin * 2

        return {containerX: containerX, containerY: containerY, containerWidth: containerWidth, containerHeight: containerHeight}
    }

    private getMainContainerPositionMobile(): {containerX: number, containerY: number, containerWidth: number, containerHeight: number} {
        const itemSize = this.itemSize
        const margin = this.uiGameManager.canvasController.margin
        const containerX = 0
        const containerY = itemSize * 5
        const containerWidth = this.uiGameManager.canvasController.app.screen.width
        const containerHeight = this.goalHeight * 2 + margin * 2

        return {containerX: containerX, containerY: containerY, containerWidth: containerWidth, containerHeight: containerHeight}
    }

    private drawBackground() {
        const backgroundAlpha = 0.8
        const width = this.mainContainer.width
        const height = this.mainContainer.height
        this.mainContainer.bg = new UIGameAchievementBackgroundView(width, height)
        this.mainContainer.bg.alpha = backgroundAlpha
        this.mainContainer.addChild(this.mainContainer.bg)
    }

    private createScrollbox() {
        const {containerWidth, containerHeight} = this.renderMobileGoals ? this.getMainContainerPositionMobile() : this.getMainContainerPositionGeneric()
        const scrollBoxOptions: ScrollBoxOptions = {
            boxWidth: containerWidth,
            boxHeight: containerHeight * 0.9,
            overflowY: 'hidden',
            scrollbarBackgroundAlpha: 0,
            scrollbarSize: this.itemSize / 10,
            events: this.uiGameManager.canvasController.app.renderer.events,
        }
        this.scrollbox = new Scrollbox(scrollBoxOptions)
        this.mainContainer.addChild(this.scrollbox)
    }

    private createGoalViews() {
        const itemSize = this.itemSize
        const margin = this.uiGameManager.canvasController.margin
        const goalX = margin * 3
        const goalBaseY = 0
        const goalHeight = itemSize * 0.55
        const goalWidth = this.renderMobileGoals ? this.mainContainer.width - itemSize * 0.5 : (itemSize * 3) - goalX

        for(const [index, goal] of this.goals.entries()) {
            const goalResources = goal.requiredResources
            const newY = index == 0 ? goalBaseY : this.goalViews[index - 1].y + this.goalViews[index - 1].height
            this.goalViews.push(new UIGameBeginnerModeGoalView(goalX, newY, goalWidth, goalHeight, this.scrollbox.content, goal.text, goal.icon, goalResources))

            const shouldLeaveSpaceForInitialRequiredResources = index == 1
            if(shouldLeaveSpaceForInitialRequiredResources) this.goalViews[index].y += itemSize * 0.2
        }
    }

    private getGoals(): BeginnerModeGoal[] {
        const goals: BeginnerModeGoal[] = [
            {
                text: {key: 'strings:game.beginnerMode.goals.goal1'},
                icon: UIGameSpriteHandler.getRoadForPlayer(this.uiGameManager.gameController.myColor),
                requiredResources: [CardEnum.Lumber, CardEnum.Brick],
            },
            {
                text: {key: 'strings:game.beginnerMode.goals.goal2'},
                icon: UIGameSpriteHandler.getRoadForPlayer(this.uiGameManager.gameController.myColor),
                requiredResources: [CardEnum.Lumber, CardEnum.Brick],
            },
            {
                text: {key: 'strings:game.beginnerMode.goals.goal3'},
                icon: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_sheep),
                requiredResources: [CardEnum.Wool, CardEnum.Wool, CardEnum.Wool, CardEnum.Wool],
            },
            {
                text: {key: 'strings:game.beginnerMode.goals.goal4'},
                icon: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade),
                requiredResources: [CardEnum.Grain],
            },
            {
                text: {key: 'strings:game.beginnerMode.goals.goal5'},
                icon: UIGameSpriteHandler.getSettlementForPlayer(this.uiGameManager.gameController.myColor),
                requiredResources: [CardEnum.Lumber, CardEnum.Brick, CardEnum.Wool, CardEnum.Grain],
            },
            {
                text: {key: 'strings:game.beginnerMode.goals.goal6'},
                icon: UIGameSpriteHandler.getCityForPlayer(this.uiGameManager.gameController.myColor),
                requiredResources: [CardEnum.Grain, CardEnum.Grain, CardEnum.Ore, CardEnum.Ore, CardEnum.Ore],
            },
            {
                text: {key: 'strings:game.beginnerMode.goals.goal7'},
                icon: UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trophy),
            },
        ]
        return goals
    }

    updateGoals(numberOfRemainingGoals: number) {
        const numberOfGoalsToMarkAsComplete = this.goals.length - numberOfRemainingGoals
        for(const [currentGoalIndex, goalView] of this.goalViews.entries()) {
            const isCompletedOrCurrentGoal = currentGoalIndex < numberOfGoalsToMarkAsComplete
            if(isCompletedOrCurrentGoal) goalView.completeGoal()

            const nextGoalIndex = currentGoalIndex + 1
            if(nextGoalIndex >= this.goalViews.length) continue
            if(isCompletedOrCurrentGoal) this.goalViews[nextGoalIndex].animateUp()

            const shouldShowFirstGoalRequiredResources = currentGoalIndex == 0 && numberOfGoalsToMarkAsComplete == 0
            if(shouldShowFirstGoalRequiredResources) return goalView.showRequiredResources(this.uiGameManager.cardController.getCardInventory())

            const isCurrentGoal = nextGoalIndex == numberOfGoalsToMarkAsComplete
            if(!isCurrentGoal) continue

            const isLastGoal = nextGoalIndex == this.goalViews.length - 1
            if(isLastGoal) continue

            this.goalViews[nextGoalIndex].showRequiredResources(this.uiGameManager.cardController.getCardInventory())
        }

        if(!this.renderMobileGoals) return
        this.scrollbox.scrollTop = this.goalViews[numberOfGoalsToMarkAsComplete - 1].y
    }

    destroy() {
        for(const goalView of this.goalViews) goalView?.destroy({children: true})
        this.goalViews = []
        this.mainContainer?.destroy({children: true})
    }
}
